import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CloudRain, ArrowLeft, FileArrowDown, FilePdf, Clock, ShareNetwork, Trash, Eye, Link as LinkIcon, Lock, Copy } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import ShareReportModal from '@/components/ShareReportModal';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const MyReports = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [sharingReport, setSharingReport] = useState(null);
  const [activeTab, setActiveTab] = useState('reports');

  useEffect(() => {
    fetchReports();
    fetchShares();
  }, []);

  const fetchReports = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/reports/my`, { withCredentials: true });
      setReports(data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchShares = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/reports/shares`, { withCredentials: true });
      setShares(data);
    } catch (error) {
      // Silent - shares are optional
    }
  };

  const handleDownload = async (reportId) => {
    setDownloading(reportId);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/reports/download/${reportId}`, { withCredentials: true });
      const byteArray = new Uint8Array(data.pdf_data.match(/.{1,2}/g).map(b => parseInt(b, 16)));
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `WeatherProof-${reportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setDownloading(null);
    }
  };

  const handleRevoke = async (shareToken) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/reports/shares/${shareToken}`, { withCredentials: true });
      toast.success('Share link revoked');
      fetchShares();
    } catch (error) {
      toast.error('Failed to revoke link');
    }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/shared/${token}`);
    toast.success('Link copied');
  };

  return (
    <div className="min-h-screen bg-white mobile-nav-padding">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <CloudRain size={28} weight="bold" className="text-blue-600" />
              <span className="text-xl sm:text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
            </Link>
            <Link to="/dashboard" data-testid="back-to-dashboard-link" className="text-sm text-slate-600 hover:text-slate-950 transition-colors flex items-center gap-1.5">
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter text-slate-950" style={{ fontFamily: 'Chivo' }}>My Reports</h1>
          <p className="text-sm text-slate-600 mt-2">Download and share your weather reports</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('reports')}
            data-testid="tab-reports"
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'reports'
                ? 'border-slate-950 text-slate-950'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Reports ({reports.length})
          </button>
          <button
            onClick={() => setActiveTab('shared')}
            data-testid="tab-shared"
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
              activeTab === 'shared'
                ? 'border-slate-950 text-slate-950'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <ShareNetwork size={14} />
            Shared Links ({shares.length})
          </button>
        </div>

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-20 bg-slate-50 border border-slate-200" data-testid="no-reports-notice">
                <FilePdf size={48} weight="light" className="text-slate-300 mx-auto mb-4" />
                <p className="text-sm text-slate-500 mb-4">No reports generated yet</p>
                <Link to="/lookup" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                  Start a weather lookup
                </Link>
              </div>
            ) : (
              <div className="space-y-3" data-testid="reports-list">
                {reports.map((report) => (
                  <div
                    key={report.report_id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-5 sm:p-6 bg-white border border-slate-200 hover:border-slate-300 transition-colors gap-4"
                    data-testid={`report-row-${report.report_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
                        <FilePdf size={20} weight="bold" className="text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-950">{report.location || 'Unknown location'}</p>
                        <p className="text-xs font-mono text-slate-500 mt-0.5">{report.date} {report.time}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-slate-400 font-mono">ID: {report.report_id}</p>
                          <span className="text-slate-300">|</span>
                          <p className="text-xs text-slate-400 flex items-center gap-1">
                            <Clock size={10} />
                            {new Date(report.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 sm:flex-shrink-0">
                      <button
                        onClick={() => setSharingReport(report.report_id)}
                        data-testid={`share-btn-${report.report_id}`}
                        className="px-3 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-xs font-medium flex items-center gap-1.5"
                      >
                        <ShareNetwork size={14} weight="bold" />
                        Share
                      </button>
                      <button
                        onClick={() => handleDownload(report.report_id)}
                        disabled={downloading === report.report_id}
                        data-testid={`download-btn-${report.report_id}`}
                        className="px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 transition-colors text-xs font-medium flex items-center gap-1.5"
                      >
                        <FileArrowDown size={14} weight="bold" />
                        {downloading === report.report_id ? 'Downloading...' : 'Download'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Shared Links Tab */}
        {activeTab === 'shared' && (
          <>
            {shares.length === 0 ? (
              <div className="text-center py-20 bg-slate-50 border border-slate-200" data-testid="no-shares-notice">
                <ShareNetwork size={48} weight="light" className="text-slate-300 mx-auto mb-4" />
                <p className="text-sm text-slate-500">No shared links yet</p>
                <p className="text-xs text-slate-400 mt-1">Share a report to create a link</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="shares-list">
                {shares.map((share) => {
                  const isActive = !share.revoked && !share.expired;
                  return (
                    <div
                      key={share.share_token}
                      className={`p-5 border transition-colors ${
                        isActive ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-60'
                      }`}
                      data-testid={`share-row-${share.share_token}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium text-slate-950">{share.location || share.report_id}</p>
                            {share.password_protected && <Lock size={12} className="text-slate-400" />}
                            {share.revoked && (
                              <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 bg-red-100 text-red-600">Revoked</span>
                            )}
                            {share.expired && !share.revoked && (
                              <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-600">Expired</span>
                            )}
                            {isActive && (
                              <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-600">Active</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span className="flex items-center gap-1"><Eye size={12} /> {share.views} views</span>
                            <span className="flex items-center gap-1"><FileArrowDown size={12} /> {share.downloads} downloads</span>
                            <span className="flex items-center gap-1"><Clock size={12} /> {new Date(share.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>

                        {isActive && (
                          <div className="flex items-center gap-2 sm:flex-shrink-0">
                            <button
                              onClick={() => copyLink(share.share_token)}
                              data-testid={`copy-link-${share.share_token}`}
                              className="px-3 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-xs font-medium flex items-center gap-1.5"
                            >
                              <Copy size={14} />
                              Copy Link
                            </button>
                            <button
                              onClick={() => handleRevoke(share.share_token)}
                              data-testid={`revoke-link-${share.share_token}`}
                              className="px-3 py-2 border border-red-200 text-red-600 hover:bg-red-50 transition-colors text-xs font-medium flex items-center gap-1.5"
                            >
                              <Trash size={14} />
                              Revoke
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      {/* Share Modal */}
      {sharingReport && (
        <ShareReportModal
          reportId={sharingReport}
          onClose={() => { setSharingReport(null); fetchShares(); }}
        />
      )}
    </div>
  );
};

export default MyReports;
